import React from 'react';
import { DiabetesPageTemplate } from './DiabetesPageTemplate';
import { diabetesImages } from './diabetesPageData';

const Type1Diabetes = () => {
  return (
    <DiabetesPageTemplate
      eyebrow="Type 1 Diabetes"
      title="Type 1 Diabetes"
      intro="Type 1 diabetes is an insulin-dependent autoimmune condition in which the pancreas produces little or no insulin. It often develops in children and young adults, and symptoms usually appear quickly."
      heroSummary="With insulin, glucose monitoring, diet, exercise, and regular follow-up, people with Type 1 diabetes can lead healthy and active lives."
      heroImage={diabetesImages.img1}
      heroAlt="Type 1 diabetes care and glucose monitoring"
      sections={[
        {
          title: 'What Is Type 1 Diabetes?',
          paragraphs: [
            'Diabetes is a condition where blood sugar (glucose) rises very high, but glucose cannot move into body cells. Normally, insulin (a hormone) helps glucose enter cells. In type 1 diabetes, the pancreas produces little or no insulin, causing sugar to build up in the bloodstream.',
            'Also called insulin-dependent diabetes, it represents up to 5% of all diabetes cases, mostly in children and young adults. It is closely linked to family history, cannot be prevented, but can be managed with diet, exercise, glucose monitoring, and insulin supplementation.',
          ],
          image: diabetesImages.effectImg,
          imageAlt: 'Complications and urgency around diabetes care',
          imageCaption: 'Recognizing Type 1 diabetes early helps prevent serious complications and support fast treatment.',
          note: 'With proper care, children with type 1 diabetes can lead healthy, active lives and do everything their peers do.',
        },
        {
          title: 'What Are The Causes?',
          paragraphs: [
            'Type 1 diabetes occurs when the immune system mistakenly attacks and destroys the insulin-producing beta cells in the pancreas. This autoimmune destruction usually takes years, but symptoms appear quickly. The exact trigger is unknown, but a combination of genetic susceptibility and environmental factors such as viruses, certain bacteria, and cow’s milk is thought to play a role.',
            'Genetic risk: Having a parent or sibling with Type 1 diabetes raises the risk to around 6%, compared to less than 0.5% in the general population.',
          ],
        },
        {
          title: 'Symptoms Of Type 1 Diabetes',
          paragraphs: [
            'Type 1 diabetes develops quickly, over days or weeks, especially in children. Early recognition is critical, as about 25% of people are diagnosed during a life-threatening complication called diabetic ketoacidosis (DKA).',
          ],
          columns: [
            {
              title: 'The 4 Typical Symptoms (4Ts)',
              items: [
                'Thirst: Excessive thirst even after drinking water; waking at night to drink',
                'Thinner: Unexplained weight loss despite eating enough because the body breaks down muscle for energy',
                'Toilet: Frequent urination, bedwetting in a previously dry child, waking to urinate at night',
                'Tired: Fatigue, lack of energy, difficulty with normal activities',
              ],
            },
            {
              title: 'Other Common Symptoms',
              items: [
                'Feeling very hungry even while eating enough',
                'Blurred vision',
                'Itchiness near genitals or infections',
                'Slow wound healing',
                'Irritability and mood changes',
              ],
            },
          ],
          note: 'Diabetic ketoacidosis is a medical emergency and may include loss of appetite, nausea, vomiting, fruity smell from breath, high body temperature, and stomach pain.',
        },
        {
          title: 'Treating Type 1 Diabetes',
          paragraphs: [
            'The main goal is to keep blood sugar as stable as possible.',
          ],
          items: [
            'Insulin therapy is essential because the body produces little or no insulin.',
            'Frequent blood glucose monitoring such as finger prick tests, often 4 or more times per day.',
            'Healthy eating and carbohydrate counting to adjust insulin doses.',
            'Regular exercise, with medical advice on how to adjust insulin for activity.',
            'Regular check-ups to monitor HbA1c, blood pressure, lipids, kidney function, eyes, and feet.',
            'Self-monitoring of glucose, quitting smoking, and daily foot checks are also important to prevent complications.',
          ],
        },
      ]}
    />
  );
};

export default Type1Diabetes;
